import { Injectable } from "@angular/core";
import { DataService } from "../../data.service";
import { BillingInvoiceManualSearchItem } from "src/app/model/billing/billing-invoice-manual-search-item";
import { ReportViewer } from "src/app/model/report-viewer";
import { BillingInvoiceEditInfo } from "../../../model/billing/billing-invoice-edit-info";
import { BillingInvoiceItemEditInfo } from "../../../model/billing/billing-invoice-item-edit-info";
import { ClientFilter } from "../../../model/client-filter";
import { ClientSearch } from "src/app/model/client-search";
import { InvoiceNumberBookPoint } from "src/app/model/invoice-number-book-point";
import { BillingInvoice } from "src/app/model/billing/billing-invoice";
import { BillingInvoiceCreateResponse } from "src/app/model/billing/billing-invoice-create-response";
import { BillingManualInvoiceLogItem } from "src/app/model/billing/billing-manual-invoice-log-item";

@Injectable({
  providedIn: "root",
})
export class BillingInvoiceManualService {
  constructor(private dataService: DataService) {}

  private readonly getBillingInvoiceManualSearchItemsEndPoint: string =
    "/billingInvoiceManual/getBillingInvoiceManualSearchItems/";

  private readonly getBillingInvoiceReportEndPoint: string =
    "/billingInvoiceManual/getBillingInvoiceReport/";

  private readonly getBillingInvoiceEditInfoEndPoint: string =
    "/billingInvoiceManual/getBillingInvoiceEditInfo/";

  private readonly billingInvoiceCreateEndPoint: string =
    "/billingInvoiceManual/billingInvoiceCreate/";

  private readonly getBillingManualInvoiceLogEndPoint: string =
    "/billingInvoiceManual/GetBillingManualInvoiceLog/";

  private readonly sendInvoiceMailEndPoint: string =
    "/billingInvoiceManual/SendInvoiceMail/";

  async sendInvoiceMail(billingInvoiceId: number): Promise<void> {
    const params = { InvoiceId: billingInvoiceId };
    await this.dataService
      .post<void>(this.sendInvoiceMailEndPoint, params)
      .toPromise();
  }

  async getBillingInvoiceManualSearchItems(
    erpCode: string,
    invoiceNumber?: number,
    invoiceDateFrom?: Date,
    invoiceDateTo?: Date,
    clientId?: number,
    excludeAsoc?: boolean,
  ): Promise<BillingInvoiceManualSearchItem[]> {
    let toUtc = "";
    let fromUtc = "";

    if (invoiceDateFrom) {
      fromUtc = new Date(
        invoiceDateFrom.getFullYear(),
        invoiceDateFrom.getMonth(),
        invoiceDateFrom.getDate(),
      ).toDateString();
    }

    if (invoiceDateTo) {
      toUtc = new Date(
        invoiceDateTo.getFullYear(),
        invoiceDateTo.getMonth(),
        invoiceDateTo.getDate(),
      ).toDateString();
    }

    const params = {
      erpCode: erpCode,
      invoiceNumber: invoiceNumber,
      invoiceDateFrom: fromUtc,
      invoiceDateTo: toUtc,
      clientId: clientId,
      excludeAsoc: excludeAsoc,
    };

    return await this.dataService
      .get<
        BillingInvoiceManualSearchItem[]
      >(this.getBillingInvoiceManualSearchItemsEndPoint, params)
      .toPromise();
  }

  async getBillingInvoiceEditInfo(
    sectorId: number,
  ): Promise<BillingInvoiceEditInfo> {
    const params = { sectorId };
    return await this.dataService
      .get<BillingInvoiceEditInfo>(
        this.getBillingInvoiceEditInfoEndPoint,
        params,
      )
      .toPromise();
  }

  async getBillingInvoiceReport(
    billingInvoiceId: number,
  ): Promise<ReportViewer> {
    const params = {  billingInvoiceId };
    return await this.dataService
      .get<ReportViewer>(this.getBillingInvoiceReportEndPoint, params)
      .toPromise();
  }

  async getBillingManualInvoiceLog(
    billingManualInvoiceId: number,
  ): Promise<BillingManualInvoiceLogItem[]> {
    const params = { billingManualInvoiceId };
    return await this.dataService
      .get<
        BillingManualInvoiceLogItem[]
      >(this.getBillingManualInvoiceLogEndPoint, params)
      .toPromise();
  }

  async billingInvoiceCreate(
    clientSearch: ClientSearch,
    invoiceNumberBookPoint: InvoiceNumberBookPoint,
    billingInvoice: BillingInvoice,
    items: BillingInvoiceItemEditInfo[],
  ): Promise<BillingInvoiceCreateResponse> {
    const params = {
      IsCirculacionAbierta: billingInvoice.IsCirculacionAbierta,
      IsRejected: billingInvoice.IsRejected,
      Description: billingInvoice.Description,
      InvoiceDate: billingInvoice.InvoiceDate,
      InvoiceExpiredDate: billingInvoice.InvoiceExpiredDate,
      Amount: billingInvoice.Amount,
      InvoiceFrom: billingInvoice.InvoiceFrom,
      InvoiceTo: billingInvoice.InvoiceTo,
      InvoiceLabelType: billingInvoice.InvoiceLabelType,
      BillingInvoicePeriod: billingInvoice.BillingInvoicePeriod,
      TaxpayerType: billingInvoice.TaxpayerType,
      InvoicePoint: billingInvoice.InvoicePoint,
      ClientSearch: clientSearch,
      InvoiceTaxes: billingInvoice.InvoiceTaxes,
      InvoiceNumberBookPoint: invoiceNumberBookPoint,
      OriginalBillingInvoiceManualId:
        billingInvoice.OriginalBillingInvoiceManualId,
      OriginalBillingInvoiceId: billingInvoice.OriginalBillingInvoiceId,
      OriginalBillingInvoiceDate: billingInvoice.OriginalBillingInvoiceDate,
      OriginalAmount: billingInvoice.OriginalAmount,
      OriginalCompanyId: billingInvoice.OriginalCompanyId,
      Items: items,
    };

    return await this.dataService
      .post<BillingInvoiceCreateResponse>(
        this.billingInvoiceCreateEndPoint,
        params,
      )
      .toPromise();
  }
}
